import { Player, PlayerWithMetrics, Financial, FinancialUpdate, FinancialGoal, FinancialGoalCreate, FinancialGoalUpdate, Statistics, Whoop, WhoopConnectionRequest, WhoopConnectionResponse, WhoopSyncRequest, WhoopSyncResponse, AggregatedStatistics, APITeam, NewsSource, PlayerDocument, DocumentUpdate } from "@/types/player-types"
import type { MatchStatRow } from "@/lib/utils/statistics-utils"
import type { ComparisonPlayerRaw, ComparisonPlayerHistory } from "@/types/comparison-types"
import { AgentInvestment, AgentInvestmentCreate, AgentInvestmentUpdate, InvestmentDocumentDraft, InvestmentSummary } from "@/types/investment-types"
import { Prospect, ProspectCreate, ProspectUpdate, ProspectSummary, ProspectInteraction, ProspectInteractionCreate, ProspectAssignee } from "@/types/prospect-types"
import { NewsDigest } from "@/types/news-types"
import {
    ProfessionalContactRequest,
    ProfessionalContactRequestCreate,
    ProfessionalCreate,
    ProfessionalDetail,
    ProfessionalSummary,
    ProfessionalUpdate,
    TopicType,
} from "@/types/professional-types"
import { User } from "@/types/user-types"
import type { FanUser, FanRegisterData, FanLoginData, FanPlayerCard, FanPlayerDetail, FanPointTransaction, FanDailySummary, FanLeaderboardResponse, FanPrediction, FanPredictionWindow, FanReward, FanRewardClaim, FanSeason, FanDashboardStats, FanActionType } from "@/types/fan-types"
import type {
    RosterPlayerSummary,
    RosterPlayerDetail,
    RosterPlayerCreate,
    RosterPlayerUpdate,
    RosterMedia,
} from "@/types/roster-types"
import { RosterLockedError } from "@/types/roster-types"
import { toCamelCase, toSnakeCase } from "@/lib/case-utils"
import { authStorage } from "@/lib/auth/auth-storage"
import { IS_MOBILE_BUILD } from "@/lib/capacitor"
import { hardNavigate } from "@/lib/navigation"

interface PaginatedResponse<T> {
    items: T[];
    total: number;
}

/** Env-backed API root for mobile builds (see Vite + `.env`). */
const RAW_MOBILE_API_BASE = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "")

/**
 * On the Android emulator, `localhost` is the emulator itself, not your PC.
 * The host machine’s loopback is `10.0.2.2`. Rewrite so
 * `VITE_API_URL=http://localhost:8000` works against a backend on the
 * dev machine. Physical Android devices should use your machine’s LAN IP.
 */
function resolveMobileApiBaseUrl(): string {
    if (!RAW_MOBILE_API_BASE) return ""
    if (typeof window === "undefined") return RAW_MOBILE_API_BASE
    const cap = window.Capacitor
    if (typeof cap?.getPlatform !== "function" || cap.getPlatform() !== "android") {
        return RAW_MOBILE_API_BASE
    }
    try {
        const url = new URL(RAW_MOBILE_API_BASE)
        if (url.hostname !== "localhost" && url.hostname !== "127.0.0.1" && url.hostname !== "sigmai_backend") {
            return RAW_MOBILE_API_BASE
        }
        url.hostname = "10.0.2.2"
        return url.toString().replace(/\/$/, "")
    } catch {
        return RAW_MOBILE_API_BASE
    }
}

/**
 * Base URL for backend API calls.
 *
 * - Web: empty string. Calls hit `/api/...` and Next.js `rewrites()` proxies
 *   them to the backend (same-origin, simplest CSP, cookies still work for
 *   any session that pre-dates the token migration).
 * - Capacitor native: absolute backend URL. There is no Next.js server in
 *   the mobile bundle, and the WebView's origin is `capacitor://localhost`,
 *   so we must call the backend directly with a Bearer token.
 *
 * `VITE_API_URL` is set at build time in `.env` / CI.
 */
export function getApiBaseUrl(): string {
    if (!IS_MOBILE_BUILD) return ""
    return resolveMobileApiBaseUrl()
}

/**
 * Builds standard request headers with the Bearer token attached when available.
 * Exported so a few call-sites outside this module (legacy components) can use
 * the same token + JSON content-type contract.
 */
export const getAuthHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = {
        "Content-Type": "application/json",
    }
    const token = authStorage.getToken()
    if (token) headers["Authorization"] = `Bearer ${token}`
    return headers
}

/**
 * Auth header only (no Content-Type). Use for multipart/form-data uploads
 * (the runtime must set the boundary) and for binary preview/download fetches.
 */
export function getBearerAuthHeaders(): Record<string, string> {
    const token = authStorage.getToken()
    if (!token) return {}
    return { Authorization: `Bearer ${token}` }
}

interface LoginResponse {
    message: string
    access_token?: string
    token_type?: string
}

/**
 * In a Capacitor build, a relative `/api/...` URL resolves against the
 * WebView origin (`capacitor://localhost`) instead of the backend, which
 * fails silently (no rewrite layer) and leaves call-sites with
 * `success`-but-empty responses. Catch it loudly in development so we
 * never re-introduce that class of bug. No-op in production builds and
 * on web.
 */
function warnIfMisroutedOnMobile(response: Response): void {
    if (import.meta.env.PROD) return
    if (!IS_MOBILE_BUILD) return
    if (typeof response.url !== "string") return
    if (!response.url.startsWith("capacitor://")) return
    console.warn(
        "[api-client] Mobile fetch hit the WebView origin instead of the backend.",
        "Missing `${getApiBaseUrl()}` prefix on:",
        response.url
    )
}

async function handleResponse(response: Response) {
    warnIfMisroutedOnMobile(response)
    if (!response.ok) {
        if (response.status === 401) {
            // Drop any stale token so the next render of <AuthGuard> reflects
            // the unauthenticated state. Fire-and-forget; we redirect immediately.
            void authStorage.clearToken()
            // Skip the redirect when we're already on a public/auth page,
            // otherwise we get into a loop and clobber the login form's own
            // error rendering.
            const path = typeof window !== "undefined" ? window.location.pathname : ""
            const publicPaths = ["/login", "/set-password", "/reset-password", "/forgot-password"]
            const isAuthPage =
                publicPaths.some((page) => path === page || path.startsWith(`${page}/`)) ||
                path === "/" ||
                path.startsWith("/fan")
            if (!isAuthPage) {
                hardNavigate(`/login?sessionExpired=1`, { replace: true })
            }
            throw new Error("Unauthorized")
        }

        try {
            const error = await response.json()
            if (response.status === 422) {
                // Handle validation errors
                const details = error.detail || []
                const errorMessage = details.map((d: { msg?: string } | string) =>
                    typeof d === 'string' ? d : d.msg || ''
                ).join(", ")
                throw new Error(errorMessage || "Validation error")
            }
            // Treat expected 404s as non-errors
            if (response.status === 404 && typeof error?.detail === 'string') {
                const detail: string = error.detail
                if (detail.includes('WHOOP data not found') || detail.includes('No questionnaires found')) {
                    return null as any
                }
            }
            // Preserve backend-provided error details
            throw new Error(error.detail || error.message || response.statusText || "An error occurred")
        } catch (e) {
            // Re-throw original error if present to preserve message; otherwise fall back
            if (e instanceof Error && e.message) {
                throw e
            }
            throw new Error(response.statusText || "An error occurred")
        }
    }
    let data: any = null
    try {
        data = await response.json()
    } catch (_) {
        // No JSON body (e.g., 204 No Content)
        return null as any
    }

    return toCamelCase(data)
}

const apiClient = {
    // User authentication and profile methods
    auth: {
        login: async (email: string, password: string): Promise<LoginResponse> => {
            const body = new URLSearchParams({
                username: email,
                password,
            })

            const response = await fetch(`${getApiBaseUrl()}/api/users/login`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                body: body.toString(),
                credentials: "include",
            })

            if (!response.ok) {
                const data = await response.json().catch(() => ({}))
                throw new Error(data.detail || "Invalid login credentials")
            }

            const data: LoginResponse = await response.json()
            if (data.access_token) {
                await authStorage.setToken(data.access_token)
            }
            return data
        },

        acceptInvite: async (token: string, password: string): Promise<{ message: string }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/users/accept-invite`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token, password }),
            })
            return handleResponse(response)
        },

        forgotPassword: async (email: string): Promise<{ message: string }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/users/forgot-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            })
            return handleResponse(response)
        },

        resetPassword: async (token: string, password: string): Promise<{ message: string }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/users/reset-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token, password }),
            })
            return handleResponse(response)
        },

        logout: async (): Promise<void> => {
            try {
                await fetch(`${getApiBaseUrl()}/api/users/logout`, {
                    method: "POST",
                    headers: getAuthHeaders(),
                    credentials: "include",
                })
            } catch {
                // Best-effort: clear local session even if backend call fails.
            }
            await authStorage.clearToken()
        },
    },

    users: {
        listAgents: async (): Promise<{ id: string; fullName?: string | null; email: string }[]> => {
            const response = await fetch(`${getApiBaseUrl()}/api/users/agents`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getProfile: async (): Promise<any> => {
            const response = await fetch(`${getApiBaseUrl()}/api/users/me`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        acceptLegal: async (confidentiality = true): Promise<User> => {
            const response = await fetch(`${getApiBaseUrl()}/api/users/me/legal-acceptances`, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    accept_terms: true,
                    accept_confidentiality: confidentiality,
                }),
                credentials: "include",
            })
            return handleResponse(response)
        },

        updateProfile: async (updates: { full_name?: string; email?: string }): Promise<any> => {
            const response = await fetch(`${getApiBaseUrl()}/api/users/me`, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase(updates)),
                credentials: "include",
            })
            return handleResponse(response)
        },

        updatePassword: async (currentPassword: string, newPassword: string): Promise<any> => {
            const response = await fetch(`${getApiBaseUrl()}/api/users/me/password`, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    current_password: currentPassword,
                    new_password: newPassword,
                }),
                credentials: "include",
            })
            return handleResponse(response)
        },

        registerPushToken: async (
            token: string,
            platform: "ios" | "android"
        ): Promise<void> => {
            const response = await fetch(`${getApiBaseUrl()}/api/users/me/push-tokens`, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify({ token, platform }),
                credentials: "include",
            })
            if (!response.ok) {
                await handleResponse(response)
            }
        },

        deletePushToken: async (token: string): Promise<void> => {
            const response = await fetch(`${getApiBaseUrl()}/api/users/me/push-tokens`, {
                method: "DELETE",
                headers: getAuthHeaders(),
                body: JSON.stringify({ token }),
                credentials: "include",
            })
            if (!response.ok) {
                await handleResponse(response)
            }
        },

        deleteAccount: async (): Promise<any> => {
            const response = await fetch(`${getApiBaseUrl()}/api/users/me`, {
                method: "DELETE",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },
    },

    admin: {
        // User management
        listUsers: async (search?: string, role?: string, page_number?: number, page_size?: number, order_by?: string, order_direction?: string, all?: boolean): Promise<PaginatedResponse<User>> => {
            const params = new URLSearchParams()
            if (search) params.append("search", search)
            if (role) params.append("role", role)
            if (page_number != null) params.append("page_number", page_number.toString())
            if (page_size != null) params.append("page_size", page_size.toString())
            if (order_by) params.append("order_by", order_by)
            if (order_direction) params.append("order_direction", order_direction)
            if (all != null) params.append("all", (all) ? "true" : "false")

            const response = await fetch(`${getApiBaseUrl()}/api/admin/users?${params.toString()}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getUser: async (userId: string): Promise<any> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/users/${userId}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        createUser: async (userData: {
            email: string;
            fullName?: string;
            role: string;
            playerId?: string;
            sendInviteEmail?: boolean;
        }): Promise<{ inviteLink?: string; emailSent?: boolean }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/users`, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase(userData)),
                credentials: "include",
            })
            return handleResponse(response)
        },

        resendInvite: async (userId: string): Promise<{ inviteLink?: string; emailSent?: boolean }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/users/${userId}/resend-invite`, {
                method: "POST",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        updateUser: async (userId: string, updates: {
            email?: string;
            fullName?: string;
            role?: string;
            playerId?: string;
        }): Promise<any> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/users/${userId}`, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase(updates)),
                credentials: "include",
            })
            return handleResponse(response)
        },

        deleteUser: async (userId: string): Promise<any> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/users/${userId}`, {
                method: "DELETE",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        // Player assignments
        getUserPlayerAccess: async (userId: string): Promise<any[]> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/users/${userId}/player-access`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        assignPlayerToUser: async (userId: string, playerId: string): Promise<any> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/users/${userId}/player-access`, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify({ player_id: playerId }),
                credentials: "include",
            })
            return handleResponse(response)
        },

        removePlayerFromUser: async (userId: string, playerId: string): Promise<any> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/users/${userId}/player-access/${playerId}`, {
                method: "DELETE",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        // Player listing for assignment
        listAllPlayers: async (search?: string, position?: string, page_number?: number, page_size?: number, order_by?: string, order_direction?: string, all?: boolean): Promise<PaginatedResponse<Player>> => {
            const params = new URLSearchParams()
            if (search) params.append("search", search)
            if (position) params.append("position", position)
            if (page_number != null) params.append("page_number", page_number.toString())
            if (page_size != null) params.append("page_size", page_size.toString())
            if (order_by) params.append("order_by", order_by)
            if (order_direction) params.append("order_direction", order_direction)
            if (all != null) params.append("all", (all) ? "true" : "false")

            const response = await fetch(`${getApiBaseUrl()}/api/admin/players?${params.toString()}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        // Data refresh methods
        refreshPlayerData: async (playerId: string, dataType: 'statistics' | 'fitness' | 'news' | 'social'): Promise<any> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/refresh/player/${playerId}/${dataType}`, {
                method: "POST",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        refreshPlayerAllData: async (playerId: string): Promise<any> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/refresh/player/${playerId}/all`, {
                method: "POST",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        refreshAllPlayersData: async (dataType: 'statistics' | 'fitness' | 'news' | 'social', delay?: number): Promise<any> => {
            const params = new URLSearchParams()
            if (delay !== undefined) params.append("delay", String(delay))

            const response = await fetch(`${getApiBaseUrl()}/api/admin/refresh/all-players/${dataType}?${params.toString()}`, {
                method: "POST",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        refreshAllPlayersAllData: async (delay?: number): Promise<any> => {
            const params = new URLSearchParams()
            if (delay !== undefined) params.append("delay", String(delay))

            const response = await fetch(`${getApiBaseUrl()}/api/admin/refresh/all-players/all?${params.toString()}`, {
                method: "POST",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getRefreshStatus: async (): Promise<any> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/refresh/status`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        // Player refresh status
        getPlayersRefreshStatus: async (): Promise<any[]> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/players/refresh-status`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getPlayerRefreshStatus: async (playerId: string): Promise<any> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/players/${playerId}/refresh-status`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        // Scheduler configuration
        getSchedulerConfig: async (): Promise<any[]> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/scheduler/config`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        updateSchedulerConfig: async (dataType: string, config: any): Promise<any> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/scheduler/config/${dataType}`, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify(config),
                credentials: "include",
            })
            return handleResponse(response)
        },

        sendTestNotification: async (): Promise<any> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/notifications/test`, {
                method: "POST",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },
    },

    players: {
        getAll: async (search?: string, position?: string, nationality?: string, club?: string, page_number?: number, page_size?: number, order_by?: string, order_direction?: string, all?: boolean): Promise<PaginatedResponse<PlayerWithMetrics>> => {
            const params = new URLSearchParams()
            if (search) params.append("search", search)
            if (position) params.append("position", position)
            if (nationality) params.append("nationality", nationality)
            if (club) params.append("club", club)
            if (page_number != null) params.append("page_number", page_number.toString())
            if (page_size != null) params.append("page_size", page_size.toString())
            if (order_by) params.append("order_by", order_by)
            if (order_direction) params.append("order_direction", order_direction)
            if (all != null) params.append("all", (all) ? "true" : "false")

            const response = await fetch(`${getApiBaseUrl()}/api/players?${params.toString()}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            const data = await handleResponse(response)
            // Response is already camelCased by handleResponse
            return data
        },

        getById: async (id: string): Promise<Player> => {
            const response = await fetch(`${getApiBaseUrl()}/api/players/${id}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return toCamelCase(await handleResponse(response))
        },

        findByApiPlayerId: async (apiPlayerId: number): Promise<{ exists: boolean; id?: string; firstName?: string; lastName?: string }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/players/by-api-id/${apiPlayerId}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return toCamelCase(await handleResponse(response))
        },

        create: async (player: Partial<Player>): Promise<Player> => {
            const response = await fetch(`${getApiBaseUrl()}/api/players`, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase({ ...player, age: undefined })),
                credentials: "include",
            })
            return toCamelCase(await handleResponse(response))
        },

        update: async (id: string, player: Partial<Player>): Promise<Player> => {
            const response = await fetch(`${getApiBaseUrl()}/api/players/${id}`, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase({ ...player, age: undefined })),
                credentials: "include",
            })
            return toCamelCase(await handleResponse(response))
        },

        delete: async (id: string): Promise<void> => {
            const response = await fetch(`${getApiBaseUrl()}/api/players/${id}`, {
                method: "DELETE",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            await handleResponse(response)
        },

        search: async (query: string, position?: string, club?: string): Promise<Player[]> => {
            const params = new URLSearchParams()
            if (query) params.append("q", query)
            if (position) params.append("position", position)
            if (club) params.append("club", club)

            const response = await fetch(`${getApiBaseUrl()}/api/players/search?${params.toString()}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            const data = await handleResponse(response)
            return data.map((player: any) => toCamelCase(player))
        },

        updateApiPlayerId: async (playerId: string, apiPlayerId: number): Promise<Player> => {
            const response = await fetch(`${getApiBaseUrl()}/api/players/${playerId}/api-player-id?api_player_id=${apiPlayerId}`, {
                method: "PUT",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return toCamelCase(await handleResponse(response))
        },

        refreshAllData: async (playerId: string): Promise<any> => {
            const response = await fetch(`${getApiBaseUrl()}/api/players/${playerId}/refresh-all`, {
                method: "POST",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        positionsList: async (): Promise<string[]> => {
            const response = await fetch(`${getApiBaseUrl()}/api/players/filters/positions`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        nationalitiesList: async (): Promise<string[]> => {
            const response = await fetch(`${getApiBaseUrl()}/api/players/filters/nationalities`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        clubsList: async (): Promise<string[]> => {
            const response = await fetch(`${getApiBaseUrl()}/api/players/filters/clubs`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        recordOnboardingSteps: async (
            playerId: string,
            steps: { step: string; status: "completed" | "skipped" | "in_progress" }[],
        ) => {
            const response = await fetch(`${getApiBaseUrl()}/api/players/${playerId}/onboarding-steps`, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify({ steps }),
                credentials: "include",
            })
            return handleResponse(response)
        },

        listConnectedAgents: async (playerId: string): Promise<{ id: string; fullName?: string | null; email: string }[]> => {
            const response = await fetch(`${getApiBaseUrl()}/api/players/${playerId}/connected-agents`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        handoffStatus: async (playerId: string): Promise<{ email?: string | null; invited: boolean }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/players/${playerId}/handoff`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        setConnectedAgents: async (playerId: string, agentIds: string[]) => {
            const response = await fetch(`${getApiBaseUrl()}/api/players/${playerId}/connected-agents`, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify({ agent_ids: agentIds }),
                credentials: "include",
            })
            return handleResponse(response)
        },

        handoff: async (playerId: string, email: string): Promise<{
            email: string
            emailSent: boolean
            inviteExpiresAt: string
            inviteLink?: string | null
        }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/players/${playerId}/handoff`, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify({ email }),
                credentials: "include",
            })
            return handleResponse(response)
        },

        resendHandoff: async (playerId: string): Promise<{
            email: string
            emailSent: boolean
            inviteExpiresAt: string
            inviteLink?: string | null
        }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/players/${playerId}/handoff/resend`, {
                method: "POST",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },
    },

    userPlayerAccess: {
        list: async (userId?: string, playerId?: string) => {
            const params = new URLSearchParams()
            if (userId) params.append("userId", userId)
            if (playerId) params.append("playerId", playerId)

            const response = await fetch(`${getApiBaseUrl()}/api/user-player-access?${params.toString()}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        create: async (playerId: string) => {
            const response = await fetch(`${getApiBaseUrl()}/api/user-player-access/${playerId}`, {
                method: "POST",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        update: async (id: string, access: { accessLevel?: string }) => {
            const response = await fetch(`${getApiBaseUrl()}/api/user-player-access/${id}`, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify(access),
                credentials: "include",
            })
            return handleResponse(response)
        },

        delete: async (id: string) => {
            const response = await fetch(`${getApiBaseUrl()}/api/user-player-access/${id}`, {
                method: "DELETE",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },
    },

    performance: {
        getMetrics: async (playerId: string, startDate?: Date, endDate?: Date) => {
            const params = new URLSearchParams()
            if (startDate) params.append("start_date", startDate.toISOString().split("T")[0])
            if (endDate) params.append("end_date", endDate.toISOString().split("T")[0])

            const response = await fetch(
                `${getApiBaseUrl()}/api/performance/metrics/${playerId}?${params.toString()}`,
                {
                    headers: getAuthHeaders(),
                    credentials: "include",
                }
            )
            return handleResponse(response)
        },

        createMetrics: async (playerId: string, metrics: any) => {
            const body = toSnakeCase({
                ...metrics,
                playerId, // will be converted to player_id
                date: new Date().toISOString().split('T')[0],
            })

            const response = await fetch(`${getApiBaseUrl()}/api/performance/metrics/${playerId}`, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify(body),
                credentials: "include",
            })
            return handleResponse(response)
        },

        updateMetrics: async (metricsId: string, metrics: any) => {
            const response = await fetch(`${getApiBaseUrl()}/api/performance/metrics/${metricsId}`, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase(metrics)),
                credentials: "include",
            })
            return handleResponse(response)
        },

        deleteMetrics: async (metricsId: string) => {
            const response = await fetch(`${getApiBaseUrl()}/api/performance/metrics/${metricsId}`, {
                method: "DELETE",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getStats: async (playerId: string, season?: string) => {
            const params = new URLSearchParams()
            if (season) params.append("season", season)

            const response = await fetch(`${getApiBaseUrl()}/api/performance/${playerId}?${params.toString()}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getOverallScore: async (playerId: string) => {
            const response = await fetch(`${getApiBaseUrl()}/api/performance/overall-score/${playerId}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getScoreHistory: async (playerId: string, startDate?: Date, endDate?: Date) => {
            const params = new URLSearchParams()
            if (startDate) params.append("start_date", startDate.toISOString().split("T")[0])
            if (endDate) params.append("end_date", endDate.toISOString().split("T")[0])

            const response = await fetch(
                `${getApiBaseUrl()}/api/performance/score-history/${playerId}?${params.toString()}`,
                {
                    headers: getAuthHeaders(),
                    credentials: "include",
                },
            )
            return handleResponse(response)
        },
    },

    contracts: {
        getByPlayer: async (playerId: string) => {
            const response = await fetch(`${getApiBaseUrl()}/api/contracts/player/${playerId}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        create: async (playerId: string, contract: {
            player_id: string;
            start_date?: string | null;
            end_date?: string | null;
            salary?: number | null;
            bonus_structure?: Record<string, any> | null;
            status?: string;
            club?: string;
            api_team_id?: number | null;
        }) => {
            const response = await fetch(`${getApiBaseUrl()}/api/contracts/player/${playerId}`, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify(contract),
                credentials: "include",
            })
            return handleResponse(response)
        },

        update: async (contractId: string, updates: {
            start_date?: string | null;
            end_date?: string | null;
            salary?: number | null;
            bonus_structure?: Record<string, any> | null;
            status?: string;
            club?: string;
            api_team_id?: number | null;
        }) => {
            const response = await fetch(`${getApiBaseUrl()}/api/contracts/${contractId}`, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify(updates),
                credentials: "include",
            })
            return handleResponse(response)
        },

        delete: async (contractId: string) => {
            const response = await fetch(`${getApiBaseUrl()}/api/contracts/${contractId}`, {
                method: "DELETE",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },
    },

    mentalHealth: {
        getQuestions: async (): Promise<{
            questions: Array<{ id: number; text: string; subscale: string }>;
            subscales: Record<string, number[]>;
            likertScale: Array<{ value: number; label: string }>;
            subscaleLabels: Record<string, string>;
        }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/mental-health/questions`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        submitResponse: async (playerId: string, responses: {
            date?: string;
            item1: number;
            item2: number;
            item3: number;
            item4: number;
            item5: number;
            item6: number;
            item7: number;
            item8: number;
            item9: number;
            item10: number;
        }) => {
            const response = await fetch(`${getApiBaseUrl()}/api/mental-health/response/${playerId}`, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase(responses)),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getScores: async (playerId: string, history?: boolean): Promise<
            | { score: { overall: number; subscales: Record<string, number>; status: string }; latestDate: string | null }
            | { history: Array<{ date: string; overall: number; subscales: Record<string, number>; status: string }> }
        > => {
            const params = new URLSearchParams()
            if (history) params.append('history', 'true')
            const base = `${getApiBaseUrl()}/api/mental-health/scores/${playerId}`
            const url = params.toString() ? `${base}?${params.toString()}` : base
            const response = await fetch(url, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getLatestResponse: async (playerId: string) => {
            const response = await fetch(`${getApiBaseUrl()}/api/mental-health/response/${playerId}/latest`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },
    },

    financial: {
        getFinancial: async (playerId: string): Promise<Financial> => {
            const response = await fetch(`${getApiBaseUrl()}/api/financials/player/${playerId}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        updateFinancial: async (playerId: string, financial: FinancialUpdate): Promise<Financial> => {
            const response = await fetch(`${getApiBaseUrl()}/api/financials/player/${playerId}`, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase({
                    ...financial,
                    player_id: playerId
                })),
                credentials: "include",
            })
            return handleResponse(response)
        },

        // Financial goals
        getGoals: async (playerId: string): Promise<FinancialGoal[]> => {
            const response = await fetch(`${getApiBaseUrl()}/api/financials/player/${playerId}/goals`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        createGoal: async (playerId: string, goal: FinancialGoalCreate): Promise<FinancialGoal> => {
            const response = await fetch(`${getApiBaseUrl()}/api/financials/player/${playerId}/goals`, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase(goal)),
                credentials: "include",
            })
            return handleResponse(response)
        },

        updateGoal: async (goalId: string, goal: FinancialGoalUpdate): Promise<FinancialGoal> => {
            const response = await fetch(`${getApiBaseUrl()}/api/financials/goals/${goalId}`, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase(goal)),
                credentials: "include",
            })
            return handleResponse(response)
        },

        deleteGoal: async (goalId: string): Promise<{ message: string }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/financials/goals/${goalId}`, {
                method: "DELETE",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },
    },

    statistics: {
        getByPlayer: async (playerId: string, season?: number, competition?: string): Promise<Statistics[]> => {
            const params = new URLSearchParams()
            params.append("player_id", playerId)
            if (season) params.append("season", season.toString())
            if (competition) params.append("competition", competition)

            const response = await fetch(`${getApiBaseUrl()}/api/statistics?${params.toString()}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getSeasons: async (playerId: string): Promise<number[]> => {
            const response = await fetch(`${getApiBaseUrl()}/api/statistics/player/${playerId}/seasons`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        fetchPlayerStatisticsFromAPI: async (playerId: string, apiPlayerId?: number): Promise<{ message: string; seasons_fetched: number[]; statistics: Statistics[] }> => {
            const params = new URLSearchParams()
            if (typeof apiPlayerId === 'number') {
                params.append('api_player_id', String(apiPlayerId))
            }
            const base = `${getApiBaseUrl()}/api/statistics/fetch-player-statistics-from-api/${playerId}`
            const url = params.toString() ? `${base}?${params.toString()}` : base
            const response = await fetch(url, {
                method: "POST",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        searchApiProfiles: async (playerId: string, search: string): Promise<{ candidates: Array<{ id: number; name: string; birth_date?: string; nationality?: string; photo?: string; teams?: string[]; score?: number }> }> => {
            const params = new URLSearchParams()
            params.append('search', search)
            const response = await fetch(`${getApiBaseUrl()}/api/statistics/player/${playerId}/api-profiles?${params.toString()}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getSeasonAggregated: async (playerId: string, season: number): Promise<AggregatedStatistics> => {
            const response = await fetch(`${getApiBaseUrl()}/api/statistics/player/${playerId}/season/${season}/aggregated`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getSeasonMatches: async (playerId: string, season: number): Promise<{ matches: MatchStatRow[]; totalMatches: number }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/statistics/player/${playerId}/season/${season}/matches`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        fetchPlayerTeamsFromAPI: async (playerId: string, apiPlayerId: number): Promise<{ message: string; teamsFetched: APITeam[] }> => {
            const params = new URLSearchParams()
            params.append('api_player_id', String(apiPlayerId))
            const url = `${getApiBaseUrl()}/api/statistics/fetch-player-teams-from-api/${playerId}?${params.toString()}`
            const response = await fetch(url, {
                method: "GET",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },
    },

    whoop: {
        getByPlayer: async (playerId: string): Promise<Whoop> => {
            const response = await fetch(`${getApiBaseUrl()}/api/whoop/player/${playerId}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        connect: async (request: WhoopConnectionRequest): Promise<WhoopConnectionResponse> => {
            const response = await fetch(`${getApiBaseUrl()}/api/whoop/connect`, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase(request)),
                credentials: "include",
            })
            return handleResponse(response)
        },

        sync: async (request: WhoopSyncRequest): Promise<WhoopSyncResponse> => {
            const response = await fetch(`${getApiBaseUrl()}/api/whoop/sync`, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase(request)),
                credentials: "include",
            })
            return handleResponse(response)
        },

        disconnect: async (playerId: string): Promise<{ success: boolean; message: string }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/whoop/disconnect/${playerId}`, {
                method: "POST",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        update: async (playerId: string, updates: Partial<Whoop>): Promise<Whoop> => {
            const response = await fetch(`${getApiBaseUrl()}/api/whoop/${playerId}`, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase(updates)),
                credentials: "include",
            })
            return handleResponse(response)
        },

        history: async (
            playerId: string,
            options?: { startDate?: string; endDate?: string }
        ): Promise<Whoop[]> => {
            const params = new URLSearchParams()
            if (options?.startDate) params.append("start_date", options.startDate)
            if (options?.endDate) params.append("end_date", options.endDate)
            const path = params.toString()
                ? `/api/whoop/history/${playerId}?${params.toString()}`
                : `/api/whoop/history/${playerId}`

            const response = await fetch(`${getApiBaseUrl()}${path}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getWeeklyAverages: async (playerId: string): Promise<{
            recovery: number | null;
            sleepEfficiency: number | null;
            strain: number | null;
            totalSleep: number | null;
            hrv: number | null;
            restingHeartRate: number | null;
            deepSleep: number | null;
            calories: number | null;
            daysCount: number;
        }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/whoop/weekly-averages/${playerId}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },
    },

    contact: {
        send: async (payload: { name: string; email: string; message: string; hp_check?: string }): Promise<{ message: string }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/contact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            })
            return handleResponse(response)
        },
    },

    news: {
        latest: async (playerId: string): Promise<NewsDigest> => {
            const params = new URLSearchParams()
            params.append("player_id", playerId)
            const response = await fetch(`${getApiBaseUrl()}/api/news/latest?${params.toString()}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        refresh: async (args: { playerId: string; playerName: string; club?: string; days?: number; strictSources?: boolean }): Promise<NewsDigest> => {
            const params = new URLSearchParams()
            params.append("player_id", args.playerId)
            params.append("player_name", args.playerName)
            if (args.club) params.append("club", args.club)
            if (typeof args.days === 'number') params.append("days", String(args.days))
            if (typeof args.strictSources === 'boolean') params.append("strict_sources", String(args.strictSources))

            const response = await fetch(`${getApiBaseUrl()}/api/news/refresh?${params.toString()}`, {
                method: 'POST',
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        listSources: async (playerId: string): Promise<NewsSource[]> => {
            const response = await fetch(`${getApiBaseUrl()}/api/news/sources/${playerId}`, {
                method: "GET",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        updateSources: async (playerId: string, sources: NewsSource[]): Promise<{
            success: boolean;
            message: string
        }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/news/sources/${playerId}`, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase(sources)),
                credentials: "include",
            })
            return handleResponse(response)
        },
    },

    professionals: {
        list: async (topic?: TopicType): Promise<ProfessionalSummary[]> => {
            const params = new URLSearchParams()
            if (topic) params.append("topic", topic)
            const qs = params.toString()
            const response = await fetch(
                `${getApiBaseUrl()}/api/professionals${qs ? `?${qs}` : ""}`,
                {
                    headers: getAuthHeaders(),
                    credentials: "include",
                }
            )
            const data = await handleResponse(response)
            return toCamelCase(data) as ProfessionalSummary[]
        },

        get: async (slug: string): Promise<ProfessionalDetail> => {
            const response = await fetch(
                `${getApiBaseUrl()}/api/professionals/${encodeURIComponent(slug)}`,
                {
                    headers: getAuthHeaders(),
                    credentials: "include",
                }
            )
            const data = await handleResponse(response)
            return toCamelCase(data) as ProfessionalDetail
        },

        listAdmin: async (): Promise<ProfessionalSummary[]> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/professionals`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            const data = await handleResponse(response)
            return toCamelCase(data) as ProfessionalSummary[]
        },

        getAdmin: async (id: string): Promise<ProfessionalDetail> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/professionals/${id}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            const data = await handleResponse(response)
            return toCamelCase(data) as ProfessionalDetail
        },

        create: async (payload: ProfessionalCreate): Promise<ProfessionalDetail> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/professionals`, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase(payload)),
                credentials: "include",
            })
            const data = await handleResponse(response)
            return toCamelCase(data) as ProfessionalDetail
        },

        update: async (id: string, payload: ProfessionalUpdate): Promise<ProfessionalDetail> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/professionals/${id}`, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase(payload)),
                credentials: "include",
            })
            const data = await handleResponse(response)
            return toCamelCase(data) as ProfessionalDetail
        },

        delete: async (id: string): Promise<void> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/professionals/${id}`, {
                method: "DELETE",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            await handleResponse(response)
        },

        reorder: async (
            items: { id: string; sortOrder: number }[]
        ): Promise<ProfessionalSummary[]> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/professionals/reorder`, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase({ items })),
                credentials: "include",
            })
            const data = await handleResponse(response)
            return toCamelCase(data) as ProfessionalSummary[]
        },

        uploadPhoto: async (id: string, file: File): Promise<ProfessionalDetail> => {
            const formData = new FormData()
            formData.append("file", file)
            const response = await fetch(`${getApiBaseUrl()}/api/admin/professionals/${id}/photo`, {
                method: "POST",
                body: formData,
                credentials: "include",
                headers: getBearerAuthHeaders(),
            })
            const data = await handleResponse(response)
            return toCamelCase(data) as ProfessionalDetail
        },

        deletePhoto: async (id: string): Promise<ProfessionalDetail> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/professionals/${id}/photo`, {
                method: "DELETE",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            const data = await handleResponse(response)
            return toCamelCase(data) as ProfessionalDetail
        },
    },

    professionalContacts: {
        create: async (request: ProfessionalContactRequestCreate): Promise<ProfessionalContactRequest> => {
            const response = await fetch(`${getApiBaseUrl()}/api/professional-contacts`, {
                method: 'POST',
                headers: getAuthHeaders(),
                credentials: "include",
                body: JSON.stringify(toSnakeCase(request)),
            })
            const data = await handleResponse(response)
            return toCamelCase(data) as ProfessionalContactRequest
        },

        list: async (playerId?: string, search?: string, statusFilter?: string, topicFilter?: string, page_number?: number, page_size?: number, order_by?: string, order_direction?: string, all?: boolean): Promise<PaginatedResponse<ProfessionalContactRequest>> => {
            const params = new URLSearchParams()
            if (playerId) params.append("player_id", playerId)
            if (search) params.append("search", search)
            if (statusFilter) params.append("status", statusFilter)
            if (topicFilter) params.append("topic", topicFilter)
            if (page_number != null) params.append("page_number", page_number.toString())
            if (page_size != null) params.append("page_size", page_size.toString())
            if (order_by) params.append("order_by", order_by)
            if (order_direction) params.append("order_direction", order_direction)
            if (all != null) params.append("all", (all) ? "true" : "false")

            const response = await fetch(`${getApiBaseUrl()}/api/professional-contacts?${params.toString()}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            const data = await handleResponse(response)
            return {
                items: toCamelCase(data.items),
                total: data.total
            } as PaginatedResponse<ProfessionalContactRequest>
        },

        updateStatus: async (requestId: string, status: string): Promise<ProfessionalContactRequest> => {
            const response = await fetch(`${getApiBaseUrl()}/api/professional-contacts/${requestId}/status`, {
                method: 'PATCH',
                headers: getAuthHeaders(),
                body: JSON.stringify({ status }),
                credentials: "include",
            })
            const data = await handleResponse(response)
            return toCamelCase(data) as ProfessionalContactRequest
        },

        delete: async (requestId: string): Promise<void> => {
            const response = await fetch(`${getApiBaseUrl()}/api/professional-contacts/${requestId}`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
                credentials: "include",
            })
            await handleResponse(response)
        },
    },

    instagram: {
        getByPlayer: async (playerId: string): Promise<{
            isConnected: boolean;
            igUserId?: string;
            igUsername?: string;
            followersCount?: number;
            mediaCount?: number;
            biography?: string;
            profilePictureUrl?: string;
            lastSync?: string;
        }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/instagram/player/${playerId}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        connect: async (request: { playerId: string; client?: "web" | "native"; returnPath?: string }): Promise<{
            success: boolean;
            message: string;
            authUrl?: string;
        }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/instagram/connect`, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase(request)),
                credentials: "include",
            })
            return handleResponse(response)
        },

        sync: async (request: { playerId: string }): Promise<{
            success: boolean;
            message: string;
            data?: any;
        }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/instagram/sync`, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase(request)),
                credentials: "include",
            })
            return handleResponse(response)
        },

        disconnect: async (playerId: string): Promise<{ success: boolean; message: string }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/instagram/disconnect/${playerId}`, {
                method: "POST",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getPosts: async (playerId: string, limit: number = 25): Promise<{
            posts: Array<{
                id: string;
                caption?: string;
                mediaType?: string;
                mediaUrl?: string;
                permalink?: string;
                timestamp?: string;
                likeCount?: number;
                commentsCount?: number;
                reach?: number;
                views?: number;
            }>;
            total: number;
        }> => {
            const params = new URLSearchParams()
            params.append("limit", String(limit))
            const response = await fetch(`${getApiBaseUrl()}/api/instagram/player/${playerId}/posts?${params.toString()}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getInsights: async (playerId: string): Promise<{
            reach?: number;
            profileViews?: number;
            websiteClicks?: number;
            accountsEngaged?: number;
            totalInteractions?: number;
            followerCount?: number;
        }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/instagram/player/${playerId}/insights`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getDemographics: async (playerId: string, breakdown: 'country' | 'city' | 'age' | 'gender' = 'country'): Promise<{
            breakdown: string;
            data: Record<string, number>;
        }> => {
            const params = new URLSearchParams()
            params.append("breakdown", breakdown)
            const response = await fetch(`${getApiBaseUrl()}/api/instagram/player/${playerId}/demographics?${params.toString()}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getAllDemographics: async (playerId: string): Promise<{
            country?: Record<string, number>;
            city?: Record<string, number>;
            age?: Record<string, number>;
            gender?: Record<string, number>;
        }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/instagram/player/${playerId}/demographics/all`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getWeeklyMetrics: async (playerId: string): Promise<{
            weekStartDate?: string;
            weekEndDate?: string;
            followerCountStart?: number;
            followerCountEnd?: number;
            postsCount?: number;
            storiesCount?: number;
            totalLikes?: number;
            totalComments?: number;
            totalStoryReach?: number;
            totalStoryReplies?: number;
            totalStoryInteractions?: number;
            avgEngagementRate?: number;
            avgPostEngagementRate?: number;
            avgStoryEngagementRate?: number;
            avgPostValueEur?: number;
            avgStoryValueEur?: number;
            avgReach?: number;
            avgStoryReach?: number;
            avgLikes?: number;
            avgFollowerGrowth?: number;
            avgComments?: number;
            avgBrandInterest?: number;
            avgImpact?: number;
            avgPostImpact?: number;
            avgStoryImpact?: number;
            postEngagementScore?: number;
            storyEngagementScore?: number;
            growthScore?: number;
            postImpactScore?: number;
            storyImpactScore?: number;
            engagementScore?: number;
            impactScore?: number;
        }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/instagram/player/${playerId}/weekly`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getHistory: async (playerId: string, days: number = 30): Promise<{
            snapshots: Array<{
                date: string;
                followerCount?: number;
                reach?: number;
                totalInteractions?: number;
            }>;
        }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/instagram/player/${playerId}/history?days=${days}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getStories: async (playerId: string, days: number = 30): Promise<{
            stories: Array<{
                id: string;
                mediaId: string;
                reach?: number;
                impressions?: number;
                replies?: number;
                exits?: number;
                tapsForward?: number;
                tapsBack?: number;
                mediaUrl?: string;
                permalink?: string;
                timestamp?: string;
                insightReceivedAt?: string;
                engagement?: number;
            }>;
            total: number;
        }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/instagram/player/${playerId}/stories?days=${days}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },
    },

    investments: {
        list: async (filters?: {
            playerIds?: string[]
            dateFrom?: Date | null
            dateTo?: Date | null
        }): Promise<AgentInvestment[]> => {
            const params = new URLSearchParams()
            if (filters?.playerIds?.length) params.append("player_ids", filters.playerIds.join(","))
            if (filters?.dateFrom) params.append("date_from", filters.dateFrom.toISOString())
            if (filters?.dateTo) params.append("date_to", filters.dateTo.toISOString())

            const response = await fetch(`${getApiBaseUrl()}/api/investments?${params.toString()}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getByPlayer: async (playerId: string): Promise<AgentInvestment[]> => {
            const response = await fetch(`${getApiBaseUrl()}/api/investments/player/${playerId}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getSummary: async (filters?: {
            playerIds?: string[]
            dateFrom?: Date | null
            dateTo?: Date | null
        }): Promise<InvestmentSummary> => {
            const params = new URLSearchParams()
            if (filters?.playerIds?.length) params.append("player_ids", filters.playerIds.join(","))
            if (filters?.dateFrom) params.append("date_from", filters.dateFrom.toISOString())
            if (filters?.dateTo) params.append("date_to", filters.dateTo.toISOString())

            const response = await fetch(`${getApiBaseUrl()}/api/investments/summary?${params.toString()}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        create: async (data: AgentInvestmentCreate): Promise<AgentInvestment> => {
            const response = await fetch(`${getApiBaseUrl()}/api/investments`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase(data)),
                credentials: "include",
            })
            return handleResponse(response)
        },

        update: async (id: string, data: AgentInvestmentUpdate): Promise<AgentInvestment> => {
            const response = await fetch(`${getApiBaseUrl()}/api/investments/${id}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase(data)),
                credentials: "include",
            })
            return handleResponse(response)
        },

        delete: async (id: string): Promise<void> => {
            const response = await fetch(`${getApiBaseUrl()}/api/investments/${id}`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
                credentials: "include",
            })
            await handleResponse(response)
        },

        parseDocument: async (file: File): Promise<InvestmentDocumentDraft> => {
            const formData = new FormData()
            formData.append("file", file)
            const response = await fetch(`${getApiBaseUrl()}/api/investments/parse-document`, {
                method: "POST",
                body: formData,
                credentials: "include",
                headers: getBearerAuthHeaders(),
            })
            return handleResponse(response)
        },

        uploadReceipt: async (id: string, file: File): Promise<AgentInvestment> => {
            const formData = new FormData()
            formData.append("file", file)
            const response = await fetch(`${getApiBaseUrl()}/api/investments/${id}/receipt`, {
                method: "POST",
                body: formData,
                credentials: "include",
                headers: getBearerAuthHeaders(),
            })
            return handleResponse(response)
        },

        fetchReceipt: async (id: string, signal?: AbortSignal): Promise<{ blob: Blob; contentType: string }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/investments/${id}/receipt`, {
                headers: getBearerAuthHeaders(),
                credentials: "include",
                signal,
            })
            if (!response.ok) {
                await handleResponse(response)
            }
            const blob = await response.blob()
            const header = response.headers.get("content-type")?.split(";")[0]?.trim()
            return { blob, contentType: header || blob.type || "application/octet-stream" }
        },
    },

    prospects: {
        list: async (filters?: {
            status?: string
            priority?: string
            hasAgent?: boolean
        }): Promise<Prospect[]> => {
            const params = new URLSearchParams()
            if (filters?.status) params.append("status", filters.status)
            if (filters?.priority) params.append("priority", filters.priority)
            if (filters?.hasAgent !== undefined) params.append("has_agent", String(filters.hasAgent))

            const response = await fetch(`${getApiBaseUrl()}/api/prospects?${params.toString()}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getSummary: async (): Promise<ProspectSummary> => {
            const response = await fetch(`${getApiBaseUrl()}/api/prospects/summary`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        get: async (id: string): Promise<Prospect> => {
            const response = await fetch(`${getApiBaseUrl()}/api/prospects/${id}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        create: async (data: ProspectCreate): Promise<Prospect> => {
            const response = await fetch(`${getApiBaseUrl()}/api/prospects`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase(data)),
                credentials: "include",
            })
            return handleResponse(response)
        },

        update: async (id: string, data: ProspectUpdate): Promise<Prospect> => {
            const response = await fetch(`${getApiBaseUrl()}/api/prospects/${id}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase(data)),
                credentials: "include",
            })
            return handleResponse(response)
        },

        delete: async (id: string): Promise<void> => {
            const response = await fetch(`${getApiBaseUrl()}/api/prospects/${id}`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
                credentials: "include",
            })
            await handleResponse(response)
        },

        // Interactions
        listInteractions: async (prospectId: string): Promise<ProspectInteraction[]> => {
            const response = await fetch(`${getApiBaseUrl()}/api/prospects/${prospectId}/interactions`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        createInteraction: async (prospectId: string, data: ProspectInteractionCreate): Promise<ProspectInteraction> => {
            const response = await fetch(`${getApiBaseUrl()}/api/prospects/${prospectId}/interactions`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase(data)),
                credentials: "include",
            })
            return handleResponse(response)
        },

        deleteInteraction: async (prospectId: string, interactionId: string): Promise<void> => {
            const response = await fetch(`${getApiBaseUrl()}/api/prospects/${prospectId}/interactions/${interactionId}`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
                credentials: "include",
            })
            await handleResponse(response)
        },

        listAgents: async (): Promise<ProspectAssignee[]> => {
            const response = await fetch(`${getApiBaseUrl()}/api/prospects/agents`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        addAssignee: async (prospectId: string, userId: string): Promise<Prospect> => {
            const response = await fetch(`${getApiBaseUrl()}/api/prospects/${prospectId}/assignees`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ user_id: userId }),
                credentials: "include",
            })
            return handleResponse(response)
        },

        removeAssignee: async (prospectId: string, userId: string): Promise<Prospect> => {
            const response = await fetch(`${getApiBaseUrl()}/api/prospects/${prospectId}/assignees/${userId}`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },
    },

    roster: {
        listPublic: async (): Promise<RosterPlayerSummary[]> => {
            const response = await fetch(`${getApiBaseUrl()}/api/public/roster`, {
                credentials: "include",
            })
            return handleResponse(response)
        },

        getPublic: async (slug: string): Promise<RosterPlayerDetail> => {
            const response = await fetch(`${getApiBaseUrl()}/api/public/roster/${encodeURIComponent(slug)}`, {
                credentials: "include",
            })
            return handleResponse(response)
        },

        getShared: async (shareToken: string): Promise<RosterPlayerDetail> => {
            const response = await fetch(
                `${getApiBaseUrl()}/api/public/roster/s/${encodeURIComponent(shareToken)}`,
                { credentials: "include" }
            )
            if (response.status === 403) {
                throw new RosterLockedError()
            }
            return handleResponse(response)
        },

        unlockShared: async (shareToken: string, password: string): Promise<RosterPlayerDetail> => {
            const response = await fetch(
                `${getApiBaseUrl()}/api/public/roster/s/${encodeURIComponent(shareToken)}/unlock`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({ password }),
                }
            )
            if (response.status === 403) {
                throw new Error("Incorrect password")
            }
            if (response.status === 429) {
                throw new Error("Too many attempts. Try again in a minute.")
            }
            return handleResponse(response)
        },

        listAdmin: async (): Promise<RosterPlayerSummary[]> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/roster`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        getAdmin: async (playerId: string): Promise<RosterPlayerDetail> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/roster/${playerId}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        create: async (data: RosterPlayerCreate): Promise<RosterPlayerDetail> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/roster`, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase(data)),
                credentials: "include",
            })
            return handleResponse(response)
        },

        update: async (playerId: string, data: RosterPlayerUpdate): Promise<RosterPlayerDetail> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/roster/${playerId}`, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase(data)),
                credentials: "include",
            })
            return handleResponse(response)
        },

        delete: async (playerId: string): Promise<void> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/roster/${playerId}`, {
                method: "DELETE",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            await handleResponse(response)
        },

        reorder: async (
            items: { id: string; sortOrder: number }[]
        ): Promise<RosterPlayerSummary[]> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/roster/reorder`, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase({ items })),
                credentials: "include",
            })
            return handleResponse(response)
        },

        uploadMedia: async (
            playerId: string,
            file: File,
            kind: "main" | "gallery" | "video" | "club_logo",
            sortOrder = 0
        ): Promise<RosterMedia> => {
            const formData = new FormData()
            formData.append("file", file)
            formData.append("kind", kind)
            formData.append("sort_order", String(sortOrder))

            const response = await fetch(`${getApiBaseUrl()}/api/admin/roster/${playerId}/media`, {
                method: "POST",
                body: formData,
                credentials: "include",
                headers: getBearerAuthHeaders(),
            })
            return handleResponse(response)
        },

        addExternalVideo: async (
            playerId: string,
            externalUrl: string,
            sortOrder = 0
        ): Promise<RosterMedia> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/roster/${playerId}/media/external`, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase({ externalUrl, sortOrder })),
                credentials: "include",
            })
            return handleResponse(response)
        },

        reorderMedia: async (
            playerId: string,
            items: { id: string; sortOrder: number }[]
        ): Promise<RosterMedia[]> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/roster/${playerId}/media/reorder`, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase({ items })),
                credentials: "include",
            })
            return handleResponse(response)
        },

        deleteMedia: async (mediaId: string): Promise<void> => {
            const response = await fetch(`${getApiBaseUrl()}/api/admin/roster/media/${mediaId}`, {
                method: "DELETE",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            await handleResponse(response)
        },
    },

    documents: {
        getByPlayer: async (playerId: string, category?: string): Promise<PlayerDocument[]> => {
            const params = new URLSearchParams()
            if (category) params.set("category", category)
            const qs = params.toString()
            const response = await fetch(`${getApiBaseUrl()}/api/documents/player/${playerId}${qs ? `?${qs}` : ""}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        upload: async (playerId: string, file: File, name: string, category: string): Promise<PlayerDocument> => {
            const formData = new FormData()
            formData.append("file", file)
            formData.append("name", name)
            formData.append("category", category)

            const response = await fetch(`${getApiBaseUrl()}/api/documents/player/${playerId}/upload`, {
                method: "POST",
                body: formData,
                credentials: "include",
                headers: getBearerAuthHeaders(),
            })
            return handleResponse(response)
        },

        update: async (documentId: string, data: DocumentUpdate): Promise<PlayerDocument> => {
            const response = await fetch(`${getApiBaseUrl()}/api/documents/${documentId}`, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify(toSnakeCase(data)),
                credentials: "include",
            })
            return handleResponse(response)
        },

        delete: async (documentId: string): Promise<void> => {
            const response = await fetch(`${getApiBaseUrl()}/api/documents/${documentId}`, {
                method: "DELETE",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            await handleResponse(response)
        },

    },

    comparison: {
        getPlayerStats: async (apiPlayerId: number, season: number) => {
            const params = new URLSearchParams({ season: season.toString() })
            const response = await fetch(`${getApiBaseUrl()}/api/comparison/player/${apiPlayerId}/stats?${params}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response) as Promise<ComparisonPlayerRaw>
        },

        getPlayerHistory: async (apiPlayerId: number, minSeason: number, currentSeason: number) => {
            const params = new URLSearchParams({
                min_season: minSeason.toString(),
                current_season: currentSeason.toString(),
            })
            const response = await fetch(`${getApiBaseUrl()}/api/comparison/player/${apiPlayerId}/history?${params}`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response) as Promise<ComparisonPlayerHistory>
        },
    },

    fan: {
        auth: {
            login: async (data: FanLoginData): Promise<LoginResponse> => {
                const response = await fetch(`${getApiBaseUrl()}/api/fan/auth/login`, {
                    method: "POST",
                    headers: getAuthHeaders(),
                    body: JSON.stringify(data),
                    credentials: "include",
                })
                if (!response.ok) {
                    const err = await response.json().catch(() => ({}))
                    throw new Error(err.detail || "Invalid credentials")
                }
                const body: LoginResponse = await response.json()
                if (body.access_token) {
                    await authStorage.setToken(body.access_token)
                }
                return body
            },

            logout: async (): Promise<void> => {
                try {
                    await fetch(`${getApiBaseUrl()}/api/fan/auth/logout`, {
                        method: "POST",
                        headers: getAuthHeaders(),
                        credentials: "include",
                    })
                } catch {
                    // Best-effort: clear local session even if backend call fails.
                }
                await authStorage.clearToken()
            },

            getProfile: async (): Promise<FanUser | null> => {
                const response = await fetch(`${getApiBaseUrl()}/api/fan/auth/me`, {
                    headers: getAuthHeaders(),
                    credentials: "include",
                })
                if (response.status === 401) return null
                return handleResponse(response)
            },

            updateProfile: async (updates: { displayName?: string; avatarUrl?: string; favoriteTeam?: string }): Promise<FanUser> => {
                const response = await fetch(`${getApiBaseUrl()}/api/fan/auth/me`, {
                    method: "PUT",
                    headers: getAuthHeaders(),
                    body: JSON.stringify(toSnakeCase(updates)),
                    credentials: "include",
                })
                return handleResponse(response)
            },
        },

        players: {
            getSeasonRoster: async (): Promise<FanPlayerCard[]> => {
                const response = await fetch(`${getApiBaseUrl()}/api/fan/players/roster`, {
                    headers: getAuthHeaders(),
                    credentials: "include",
                })
                return handleResponse(response)
            },

            getPlayerDetail: async (playerId: string): Promise<FanPlayerDetail> => {
                const response = await fetch(`${getApiBaseUrl()}/api/fan/players/${playerId}`, {
                    headers: getAuthHeaders(),
                    credentials: "include",
                })
                return handleResponse(response)
            },
        },

        engage: {
            recordAction: async (actionType: FanActionType, metadata?: Record<string, unknown>): Promise<FanPointTransaction> => {
                const response = await fetch(`${getApiBaseUrl()}/api/fan/engage/action`, {
                    method: "POST",
                    headers: getAuthHeaders(),
                    body: JSON.stringify(toSnakeCase({ actionType, metadata })),
                    credentials: "include",
                })
                return handleResponse(response)
            },

            getDailySummary: async (): Promise<FanDailySummary> => {
                const response = await fetch(`${getApiBaseUrl()}/api/fan/engage/daily-summary`, {
                    headers: getAuthHeaders(),
                    credentials: "include",
                })
                return handleResponse(response)
            },

            getHistory: async (limit = 50, offset = 0): Promise<FanPointTransaction[]> => {
                const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() })
                const response = await fetch(`${getApiBaseUrl()}/api/fan/engage/history?${params}`, {
                    headers: getAuthHeaders(),
                    credentials: "include",
                })
                return handleResponse(response)
            },
        },

        leaderboard: {
            get: async (seasonSlug?: string, limit = 50, offset = 0): Promise<FanLeaderboardResponse> => {
                const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() })
                if (seasonSlug) params.append("season_slug", seasonSlug)
                const response = await fetch(`${getApiBaseUrl()}/api/fan/leaderboard?${params}`, {
                    headers: getAuthHeaders(),
                    credentials: "include",
                })
                return handleResponse(response)
            },
        },

        predictions: {
            submit: async (data: { playerId: string; weekNumber: number; predictedScore: number }): Promise<FanPrediction> => {
                const response = await fetch(`${getApiBaseUrl()}/api/fan/predictions`, {
                    method: "POST",
                    headers: getAuthHeaders(),
                    body: JSON.stringify(toSnakeCase(data)),
                    credentials: "include",
                })
                return handleResponse(response)
            },

            getMine: async (seasonSlug?: string): Promise<FanPrediction[]> => {
                const params = new URLSearchParams()
                if (seasonSlug) params.append("season_slug", seasonSlug)
                const response = await fetch(`${getApiBaseUrl()}/api/fan/predictions/mine?${params}`, {
                    headers: getAuthHeaders(),
                    credentials: "include",
                })
                return handleResponse(response)
            },

            getAvailable: async (): Promise<FanPredictionWindow> => {
                const response = await fetch(`${getApiBaseUrl()}/api/fan/predictions/available`, {
                    headers: getAuthHeaders(),
                    credentials: "include",
                })
                return handleResponse(response)
            },
        },

        rewards: {
            getCatalog: async (): Promise<FanReward[]> => {
                const response = await fetch(`${getApiBaseUrl()}/api/fan/rewards/catalog`, {
                    headers: getAuthHeaders(),
                    credentials: "include",
                })
                return handleResponse(response)
            },

            claim: async (rewardId: string): Promise<FanRewardClaim> => {
                const response = await fetch(`${getApiBaseUrl()}/api/fan/rewards/claim/${rewardId}`, {
                    method: "POST",
                    headers: getAuthHeaders(),
                    credentials: "include",
                })
                return handleResponse(response)
            },

            getMyClaims: async (): Promise<FanRewardClaim[]> => {
                const response = await fetch(`${getApiBaseUrl()}/api/fan/rewards/mine`, {
                    headers: getAuthHeaders(),
                    credentials: "include",
                })
                return handleResponse(response)
            },
        },

        admin: {
            getStats: async (): Promise<FanDashboardStats> => {
                const response = await fetch(`${getApiBaseUrl()}/api/admin/fans/stats`, {
                    headers: getAuthHeaders(),
                    credentials: "include",
                })
                return handleResponse(response)
            },

            listFanUsers: async (search?: string, limit = 50, offset = 0): Promise<FanUser[]> => {
                const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() })
                if (search) params.append("search", search)
                const response = await fetch(`${getApiBaseUrl()}/api/admin/fans/users?${params}`, {
                    headers: getAuthHeaders(),
                    credentials: "include",
                })
                return handleResponse(response)
            },

            toggleFanActive: async (fanId: string): Promise<{ message: string }> => {
                const response = await fetch(`${getApiBaseUrl()}/api/admin/fans/users/${fanId}/deactivate`, {
                    method: "POST",
                    headers: getAuthHeaders(),
                    credentials: "include",
                })
                return handleResponse(response)
            },

            createFanUser: async (data: FanRegisterData): Promise<FanUser> => {
                const response = await fetch(`${getApiBaseUrl()}/api/admin/fans/users`, {
                    method: "POST",
                    headers: getAuthHeaders(),
                    body: JSON.stringify(toSnakeCase(data)),
                    credentials: "include",
                })
                return handleResponse(response)
            },

            listSeasons: async (): Promise<FanSeason[]> => {
                const response = await fetch(`${getApiBaseUrl()}/api/admin/fans/seasons`, {
                    headers: getAuthHeaders(),
                    credentials: "include",
                })
                return handleResponse(response)
            },

            createSeason: async (data: { name: string; slug: string; startDate: string; endDate: string; statsSeason?: number; playerIds?: string[] }): Promise<FanSeason> => {
                const response = await fetch(`${getApiBaseUrl()}/api/admin/fans/seasons`, {
                    method: "POST",
                    headers: getAuthHeaders(),
                    body: JSON.stringify(toSnakeCase(data)),
                    credentials: "include",
                })
                return handleResponse(response)
            },

            updateSeason: async (seasonId: string, data: { name?: string; isActive?: boolean; statsSeason?: number; playerIds?: string[] }): Promise<FanSeason> => {
                const response = await fetch(`${getApiBaseUrl()}/api/admin/fans/seasons/${seasonId}`, {
                    method: "PUT",
                    headers: getAuthHeaders(),
                    body: JSON.stringify(toSnakeCase(data)),
                    credentials: "include",
                })
                return handleResponse(response)
            },

            deleteSeason: async (seasonId: string): Promise<{ message: string }> => {
                const response = await fetch(`${getApiBaseUrl()}/api/admin/fans/seasons/${seasonId}`, {
                    method: "DELETE",
                    headers: getAuthHeaders(),
                    credentials: "include",
                })
                return handleResponse(response)
            },

            processWeek: async (seasonId: string): Promise<{ week: number; scores_processed: number; predictions_resolved: number }> => {
                const response = await fetch(`${getApiBaseUrl()}/api/admin/fans/seasons/${seasonId}/process-week`, {
                    method: "POST",
                    headers: getAuthHeaders(),
                    credentials: "include",
                })
                return handleResponse(response)
            },

            listRewards: async (): Promise<FanReward[]> => {
                const response = await fetch(`${getApiBaseUrl()}/api/admin/fans/rewards`, {
                    headers: getAuthHeaders(),
                    credentials: "include",
                })
                return handleResponse(response)
            },

            createReward: async (data: { name: string; description?: string; rewardType: string; requiredTier?: string; pointsCost?: number; imageUrl?: string; seasonId?: string }): Promise<FanReward> => {
                const response = await fetch(`${getApiBaseUrl()}/api/admin/fans/rewards`, {
                    method: "POST",
                    headers: getAuthHeaders(),
                    body: JSON.stringify(toSnakeCase(data)),
                    credentials: "include",
                })
                return handleResponse(response)
            },

            updateReward: async (rewardId: string, data: Record<string, unknown>): Promise<FanReward> => {
                const response = await fetch(`${getApiBaseUrl()}/api/admin/fans/rewards/${rewardId}`, {
                    method: "PUT",
                    headers: getAuthHeaders(),
                    body: JSON.stringify(toSnakeCase(data)),
                    credentials: "include",
                })
                return handleResponse(response)
            },

            refreshLeaderboard: async (): Promise<{ message: string }> => {
                const response = await fetch(`${getApiBaseUrl()}/api/admin/fans/refresh-leaderboard`, {
                    method: "POST",
                    headers: getAuthHeaders(),
                    credentials: "include",
                })
                return handleResponse(response)
            },
        },
    },

    advisorConsent: {
        get: async (): Promise<{ granted: boolean }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/chat/advisor-consent`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        save: async (granted: boolean): Promise<{ granted: boolean }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/chat/advisor-consent`, {
                method: "PUT",
                headers: getAuthHeaders(),
                credentials: "include",
                body: JSON.stringify({ granted }),
            })
            return handleResponse(response)
        },
    },

    playerSetup: {
        get: async (): Promise<import("@/types/player-setup").PlayerSetupDetail> => {
            const response = await fetch(`${getApiBaseUrl()}/api/player-setup`, {
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },

        saveBirthTerms: async (birthDate: string, acceptTerms: boolean) => {
            const response = await fetch(`${getApiBaseUrl()}/api/player-setup/birth-terms`, {
                method: "PUT",
                headers: getAuthHeaders(),
                credentials: "include",
                body: JSON.stringify({ birth_date: birthDate, accept_terms: acceptTerms }),
            })
            return handleResponse(response) as Promise<import("@/types/player-setup").PlayerSetupDetail>
        },

        saveAgents: async (grants: { userId: string; allow: boolean }[]) => {
            const response = await fetch(`${getApiBaseUrl()}/api/player-setup/agents`, {
                method: "PUT",
                headers: getAuthHeaders(),
                credentials: "include",
                body: JSON.stringify({
                    grants: grants.map((grant) => ({ user_id: grant.userId, allow: grant.allow })),
                }),
            })
            return handleResponse(response) as Promise<import("@/types/player-setup").PlayerSetupDetail>
        },

        saveConsents: async (body: {
            publicCareer: boolean
            whoop: boolean
            motivation: boolean
            aiAssistant: boolean
            improveModels: boolean
            markStarted?: boolean
        }) => {
            const response = await fetch(`${getApiBaseUrl()}/api/player-setup/consents`, {
                method: "PUT",
                headers: getAuthHeaders(),
                credentials: "include",
                body: JSON.stringify({
                    public_career: body.publicCareer,
                    whoop: body.whoop,
                    motivation: body.motivation,
                    ai_assistant: body.aiAssistant,
                    improve_models: body.improveModels,
                    mark_started: body.markStarted === true,
                }),
            })
            return handleResponse(response) as Promise<import("@/types/player-setup").PlayerSetupDetail>
        },

        saveFinancial: async (body: {
            netMonthlyIncome: number | null
            monthlySavings: number | null
            liquidWealth: number | null
        }) => {
            const response = await fetch(`${getApiBaseUrl()}/api/player-setup/financial`, {
                method: "PUT",
                headers: getAuthHeaders(),
                credentials: "include",
                body: JSON.stringify({
                    net_monthly_income: body.netMonthlyIncome,
                    monthly_savings: body.monthlySavings,
                    liquid_wealth: body.liquidWealth,
                }),
            })
            return handleResponse(response) as Promise<import("@/types/player-setup").PlayerSetupDetail>
        },

        finishStep: async (step: string, connected: boolean) => {
            const response = await fetch(`${getApiBaseUrl()}/api/player-setup/steps/${step}`, {
                method: "POST",
                headers: getAuthHeaders(),
                credentials: "include",
                body: JSON.stringify({ connected }),
            })
            return handleResponse(response) as Promise<import("@/types/player-setup").PlayerSetupDetail>
        },

        deleteWhoopData: async () => {
            const response = await fetch(`${getApiBaseUrl()}/api/player-setup/whoop-data`, {
                method: "DELETE",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response) as Promise<import("@/types/player-setup").PlayerSetupDetail>
        },

        deleteMotivationData: async () => {
            const response = await fetch(`${getApiBaseUrl()}/api/player-setup/motivation-data`, {
                method: "DELETE",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response) as Promise<import("@/types/player-setup").PlayerSetupDetail>
        },

        removeAgent: async (agentId: string) => {
            const response = await fetch(`${getApiBaseUrl()}/api/player-setup/agents/${agentId}`, {
                method: "DELETE",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response) as Promise<import("@/types/player-setup").PlayerSetupDetail>
        },

        remind: async (playerId: string): Promise<{ message: string; delivery: string }> => {
            const response = await fetch(`${getApiBaseUrl()}/api/player-setup/reminder/${playerId}`, {
                method: "POST",
                headers: getAuthHeaders(),
                credentials: "include",
            })
            return handleResponse(response)
        },
    },
}

export default apiClient 
